import React, { useEffect } from "react";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Box,
  Button,
  TextField,
} from "@mui/material";
import api from "../api/api";
import { ENDPOINTS } from "../api/endpoints";
import { Snackbar, Alert } from "@mui/material";

/* ---------- Helpers ---------- */

const withAllOption = (options = [], label = "ALL") => [
  { value: "ALL", label },
  ...options,
];
const applyAllLogic = (current = [], incoming = [], options = [], name) => {
  const values = options.map((o) => o.value);

  // LOCATION FIRST
  if (name === "location") {
    if (incoming.includes("NATIONWIDE")) {
      return ["NATIONWIDE"];
    }
    return incoming.filter((v) => v !== "NATIONWIDE");
  }

const anyValueMap = {
  sex: "Any Gender",
  ethnicity: "Any Ethnicity",
  payType: "Any",
};

  const anyValue = anyValueMap[name];

  if (anyValue && incoming.includes(anyValue)) {
  return [
    anyValue,
    ...options
      .map((o) => o.value)
      .filter((v) => v !== anyValue),
  ];
}
  if (anyValue && current.includes(anyValue) && !incoming.includes(anyValue)) {
    return incoming;
  }

  return incoming;
};
/* const anyOptionMap = {
  sex: { value: "ANY_GENDER", label: "Any Gender" },
  ethnicity: { value: "ANY_ETHNICITY", label: "Any Ethnicity" },
};

const optionsWithAll = anyOptionMap[name]
  ? [anyOptionMap[name], ...options]
  : options;

  const anyValueMap = {
  sex: "ANY_GENDER",
  ethnicity: "ANY_ETHNICITY",
};

const anyValue = anyValueMap[name];

if (anyValue) {
  // If user selects "Any"
  if (incoming.includes(anyValue)) {
    return options.map(o => o.value); 
  }

  // If previously "Any" was selected and now user changes
  if (current.some(v => v === anyValue)) {
    return incoming;
  }
} */

/* ---------- MultiSelect ---------- */

const MultiSelect = ({ label, name, value = [], options = [], onChange }) => {
  const allLabelMap = {
  sex: "Any Gender",
  ethnicity: "Any Ethnicity",
  payType: "Any",
};
  const excludeAllFields = [
    "location",
    "jobCategories",
    "unionStatus",
    "roleTypes",
  ];
  /* 
const optionsWithAll = excludeAllFields.includes(name)
  ? options
  : withAllOption(options, allLabelMap[name] || "ALL"); */
 const anyLabelMap = {
  sex: "Any Gender",
  ethnicity: "Any Ethnicity",
  payType: "Any",
};

const optionsWithAny =
  name === "payType"
    ? options
    : anyLabelMap[name]
      ? [{ value: anyLabelMap[name], label: anyLabelMap[name] }, ...options]
      : options;
  return (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        label={label}
        value={value}
        onChange={(e) => {
          const next = applyAllLogic(value, e.target.value, options, name);
          onChange(name, next);
        }}
        renderValue={(selected) => {
          const anyValue = allLabelMap[name];

          if (anyValue && selected.includes(anyValue)) {
            return anyValue;
          }

          return options
            .filter((o) => selected.includes(o.value))
            .map((o) => o.label)
            .join(", ");
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300, // controls dropdown height
              marginTop: 8,
            },
          },
        }}
      >
        {optionsWithAny.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            <Checkbox checked={value.includes(opt.value)} />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/* ---------- MAIN COMPONENT ---------- */

export default function SearchFilters({
  filters,
  setFilters,
  locationOptions = [],
  ethnicityOptions = [],
  unionOptions = [],
  categoryOptions = [],
  roleTypeOptions = [],
  sexOptions = [],
  paymentTypeOptions = [],
  onSearch,
  onClear,
}) {
  const email = localStorage.getItem("email") || "";
  const userId = localStorage.getItem("userId");
  const update = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const ageMin = Number.isFinite(filters.ageMin) ? filters.ageMin : 0;
  const ageMax = Number.isFinite(filters.ageMax) ? filters.ageMax : 100;

  const [toast, setToast] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });
  /* ---------------- LOAD SAVED SEARCH ---------------- */

  useEffect(() => {
    if (!email) return;

    const loadSavedSearch = async () => {
      try {
        const resp = await api.get(ENDPOINTS.getCastingSearch(), {
          params: { email },
        });

        const saved = resp.data?.data;
        if (!saved) return;

        setFilters((prev) => ({
          ...prev,
          location: saved.locations || [],
          ethnicity: saved.ethnicities || [],
          unionStatus: saved.unions || [],
          jobCategories: saved.jobCategories || [],
          roleTypes: saved.roleTypes || [],
          sex: saved.sex || [],
          payType: saved.payLevels || [],
          ageMin: saved.minAge ?? 0,
          ageMax: saved.maxAge ?? 100,
        }));

        setToast({
          open: true,
          message: "Loaded your saved search",
          severity: "info",
        });
      } catch (err) {
        console.error("Failed to load saved search", err);
      }
    };

    loadSavedSearch();
  }, [email, setFilters]);

  const sortedLocationOptions = [
    ...locationOptions.filter((o) => o.value === "NATIONWIDE"),
    ...locationOptions.filter((o) => o.value !== "NATIONWIDE"),
  ];

  /* ---------------- SAVE SEARCH ---------------- */

  const saveCastingSearch = async () => {
    if (!email) return;
    const cleanValues = (selected = [], options = [], anyLabel) => {
      if (selected.includes(anyLabel)) {
        return [anyLabel, ...options.map((o) => o.value)];
      }
      return selected;
    };

    /* const anyLabelMap = {
        sex: "Any Gender",
        ethnicity: "Any Ethnicity",
      };

      const anyLabel = anyLabelMap[name];

      if (anyLabel) {
        // If user selects "Any"
        if (incoming.includes(anyLabel)) {
          return [anyLabel, ...options.map((o) => o.value)];
        }

        // If user had "Any" before and now changes selection
        if (current.includes(anyLabel)) {
          return incoming;
        }
      }

      return incoming;
    }; */

    try {
      const payload = {
        email: email,
        locations: filters.location || [],
        ethnicities: cleanValues(
          filters.ethnicity,
          ethnicityOptions,
          "Any Ethnicity",
        ),
        unions: filters.unionStatus || [],
        jobCategories: filters.jobCategories || [],
payLevels: filters.payType?.includes("Any")
  ? paymentTypeOptions
      .filter((p) => p.value !== "Any")
      .map((p) => p.value)
  : (filters.payType || []),
        sex: cleanValues(filters.sex, sexOptions, "Any Gender"),
        roleTypes: filters.roleTypes || [],
        jobType: filters.jobType || [],
        title: "",
        minAge:
          filters.ageMin === "" || filters.ageMin == null
            ? null
            : Number(filters.ageMin),
        maxAge:
          filters.ageMax === "" || filters.ageMax == null
            ? null
            : Number(filters.ageMax),
        sortOrder: "",
        rushCall: false,
      };

      await api.post(ENDPOINTS.saveCastingSearch(), payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      setToast({
        open: true,
        message: "Search saved successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Save failed:", err.response?.data || err);
      setToast({
        open: true,
        message: "Failed to save search",
        severity: "error",
      });
    }
  };
  const ageRangeError =
    ageMin !== "" && ageMax !== "" && Number(ageMax) <= Number(ageMin);

  /* ---------------- UI ---------------- */

  return (
    <Grid container spacing={2}>
      {/* Row 1 */}
      <Grid item xs={12} md={3}>
        <MultiSelect
          label="Location"
          name="location"
          value={filters.location}
          options={sortedLocationOptions}
          onChange={update}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <MultiSelect
          label="Categories"
          name="jobCategories"
          value={filters.jobCategories}
          options={categoryOptions}
          onChange={update}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <MultiSelect
          label="Union Status"
          name="unionStatus"
          value={filters.unionStatus}
          options={unionOptions}
          onChange={update}
        />
      </Grid>

      {/* Row 2 */}
      <Grid item xs={12} md={3}>
        <MultiSelect
          label="Role Type"
          name="roleTypes"
          value={filters.roleTypes}
          options={roleTypeOptions}
          onChange={update}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <MultiSelect
          label="Gender"
          name="sex"
          value={filters.sex}
          options={sexOptions}
          onChange={update}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <MultiSelect
          label="Ethnicity"
          name="ethnicity"
          value={filters.ethnicity}
          options={ethnicityOptions}
          onChange={update}
        />
      </Grid>

      {/* AGE */}
      <Grid item xs={12} md={6}>
        <Box display="flex" gap={2}>
          <TextField
            label="Min Age"
            type="number"
            size="small"
            fullWidth
            value={ageMin}
            error={ageRangeError}
            onChange={(e) =>
              update(
                "ageMin",
                Math.max(0, Math.min(100, Number(e.target.value))),
              )
            }
          />
          <TextField
            label="Max Age"
            type="number"
            size="small"
            fullWidth
            value={ageMax}
            error={ageRangeError}
            helperText={
              ageRangeError ? "Max Age must be greater than Min Age" : ""
            }
            onChange={(e) =>
              update(
                "ageMax",
                Math.max(0, Math.min(100, Number(e.target.value))),
              )
            }
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={3}>
        <MultiSelect
          label="Payment Type"
          name="payType"
          value={filters.payType || []}
          options={paymentTypeOptions}
          onChange={update}
        />
      </Grid>

      {/* ACTIONS */}
      <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1}>
        <Button
          variant="outlined"
          onClick={() => {
            if (typeof onClear === "function") {
              onClear();
            }
          }}
        >
          Clear
        </Button>

        <Button
          variant="outlined"
          onClick={saveCastingSearch}
          disabled={ageRangeError}
        >
          Save Search
        </Button>

        <Button
          variant="contained"
          disabled={ageRangeError}
          onClick={() => typeof onSearch === "function" && onSearch()}
        >
          Search
        </Button>
      </Grid>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "middle" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast({ ...toast, open: false })}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
}
